import { isEmpty } from 'radash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/core/Button';
import { Grid } from '@/components/core/Grid';
import type { GridRef } from '@/components/core/Grid/Grid';
import { fieldPathToGridStateKey } from '@/components/core/Grid/useGridPreferences';
import { useVisibleMemberAttributes } from '@/components/datamodel/Collection/useMemberAttributes';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { logger } from '@/services/logger';
import { DataModelAttributeTypeName, Item } from '@/types/DataModel';

import { generateCollectionAttributeColumns } from '../Collection/columns';
import { filterCollection } from '../Collection/filtering';
import { generateInitialSortingState } from '../Collection/sorting';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useFocusOnNewRow } from '../hooks/useFocusOnNewRow';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { useValidationsStore } from '../hooks/useValidationsStore';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';
import { validateMultiDimensionalEntry } from './validating';

interface MultiDimensionalComponentProps extends EditorComponent {

}

interface MultiDimensionalDataType {
    [key: string]: string;
}

const MultiDimensionalComponent = ({
    attribute,
    direction = 'vertical',
    fieldPath = attribute.name,
    hideLabel,
    hidden = false,
    readonly = false,
    isKeyAttribute = false,
    beforeAttributeControl,
    labelClassName
}: MultiDimensionalComponentProps) => {

    const { addLocalValidationError, removeLocalValidationError, removeLocalValidationErrorByAttributeName } = useValidationsStore(useShallow(state => ({
        addLocalValidationError : state.addLocalValidationError,
        removeLocalValidationError: state.removeLocalValidationError,
        removeLocalValidationErrorByAttributeName: state.removeLocalValidationErrorByAttributeName,
    })));

    // Wrapper div ref: scrolls the attribute into view when the user searches
    // by attribute name in the editor header.
    const focusRef = useFocusOnAttribute<HTMLDivElement>(attribute);
    // Grid ref: also focuses the Grid's imperative handle on the same trigger,
    // so keyboard navigation lands inside the grid right after scrolling.
    const gridFocusRef = useFocusOnAttribute<GridRef>(attribute);
    const { user } = useUser();
    const { organization } = useOrganization();
    const { t } = useTranslation();
    const { getValues, setValue, unregister, formState: { isDirty }} = useFormContext();
    const { fields } = useFieldArray({
        name: fieldPath,
    });
    // Watch the actual form values to trigger validation when values change
    const watchedValues = useWatch({
        name: fieldPath,
    });
    const { dataModel } = useDataModel();

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const keyAttributeNames = attribute.keyMembers || [];

    const memberAttributes = useVisibleMemberAttributes(attribute, dataModel);

    const [enableSorting, setEnableSorting] = useState(true);
    const isAppendingRef = useRef(false);
    // Enhanced remove function with proper form state management
    const remove = (index: number) => {
        unregister(`${fieldPath}.${index}`);

        // Get current values from the form
        const currentValues = getValues(fieldPath) || [];
        // Create new array without the item at the specified index
        const newValues = currentValues.filter((_: any, i: number) => i !== index);
        // Update the form state directly
        setValue(fieldPath, newValues, { shouldDirty: true });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const columns = useMemo(() =>
        generateCollectionAttributeColumns(
            attribute,
            memberAttributes,
            fieldPath,
            fields,
            organization,
            remove,
            DataModelAttributeTypeName.MultiDimensional,
            user,
            keyAttributeNames,
            readonly,
            t,
            getValues
            // eslint-disable-next-line react-hooks/exhaustive-deps
        ), [fields, readonly, t, getValues, memberAttributes]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialSortingState = useMemo(() => generateInitialSortingState(attribute, memberAttributes), []);

    const filteredData = useMemo(() => {
        if (!dataModel || !organization || !user) {
            return fields;
        }

        const item = getValues() as Item;

        return filterCollection(fields, item, attribute, organization, user);
    }, [attribute, fields, dataModel, organization, user, getValues]);

    // Focus on the first column of newly added row
    useFocusOnNewRow({
        fields,
        fieldPath,
        memberAttributes,
        readonly,
    });

    useEffect(() => {
        // Use watchedValues if available, otherwise fall back to fields
        // watchedValues will be the actual form values and will update when any cell value changes
        const valuesToValidate = watchedValues || (fields as MultiDimensionalDataType[]);

        // Clear all local validation errors for this attribute
        // This ensures errors are cleared when validation passes or when rows are deleted
        removeLocalValidationErrorByAttributeName(fieldPath);

        if (!isEmpty(valuesToValidate)) {
            const rows = valuesToValidate as MultiDimensionalDataType[];

            rows.forEach((row, index) => {
                const rowErrors = validateMultiDimensionalEntry<MultiDimensionalDataType>(row, rows, attribute);

                removeLocalValidationError(fieldPath);
                removeLocalValidationError(`${fieldPath}.${index}`);
                if (!isEmpty(rowErrors)) {
                    rowErrors.forEach(error => {
                        addLocalValidationError(fieldPath, {
                            name: 'LOCAL_VALIDATION',
                            attributeName: fieldPath,
                            message: t(error),
                            level: 'Failure',
                            path: [attribute.name],
                        });
                        addLocalValidationError(`${fieldPath}.${index}`, {
                            name: 'LOCAL_VALIDATION',
                            attributeName: fieldPath,
                            message: t(error),
                            level: 'Failure',
                            path: [attribute.name],
                        });
                    });
                }
            });
        }
    }, [attribute, watchedValues, fields, fieldPath, addLocalValidationError, removeLocalValidationError, t, removeLocalValidationErrorByAttributeName]);

    if (hidden) {
        return null;
    }

    if (isEmpty(keyAttributeNames)) {
        logger.error(`MultiDimensionalComponent: Attribute ${attribute.name} of type ${attribute.typeName} has no key attributes`);
        return null;
    }

    const handleButtonClick = () => {
        if (isAppendingRef.current) {
            return;
        }
        isAppendingRef.current = true;
        setEnableSorting(false);
        // Get current form values
        const currentValues = getValues(fieldPath) || [];

        // Create new item and add it to the array
        const newItem = {};

        const newValues = [...currentValues, newItem];
        // Update form state directly
        setValue(fieldPath, newValues, { shouldDirty: isDirty });
        isAppendingRef.current = false;
    };

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={false}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            readonly={readonly}
            isClearable={false}
            className='tw-pb-4'
            labelClassName={labelClassName}
            actionComponent={!readonly ? <Button
                id={`${attribute.name}_button`}
                type="button"
                onClick={handleButtonClick}
            >
                {t('ADD_ENTRY')}
            </Button> : null}
        >
            <div ref={focusRef} tabIndex={-1} className="tw-overflow-auto">
                <Grid
                    columns={columns}
                    data={filteredData || []}
                    insideItemEditor={true}
                    enableSorting={enableSorting}
                    enableRowSelection={false}
                    initialSortingState={initialSortingState}
                    storedStateKey={fieldPathToGridStateKey(fieldPath)}
                    ref={gridFocusRef}
                    className='tw-bg-white'
                    maxHeight = 'tw-max-h-[384px]'
                />
            </div>
        </Layout>
    );
};

export { MultiDimensionalComponent, type MultiDimensionalDataType };
